import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { clearActowizRequest, fetchActowizProducts } from "../store/actowiz/actowizSlice";

const PLATFORM_LABELS = {
  tesco: "Tesco",
  asda: "ASDA",
  morrison: "Morrisons",
  sainsbury: "Sainsbury's",
};

function PlatformProducts({ onOpenAuth, onOpenMenu }) {
  const { platform } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const byPlatform = useAppSelector((s) => s.actowiz.byPlatform);
  const statusByPlatform = useAppSelector((s) => s.actowiz.statusByPlatform);
  const errorByPlatform = useAppSelector((s) => s.actowiz.errorByPlatform);

  const p = (platform || "").toLowerCase();
  const label = PLATFORM_LABELS[p] || "Products";

  const [page, setPage] = useState(1);
  const offset = 20;

  useEffect(() => {
    setPage(1);
  }, [p]);

  useEffect(() => {
    if (!p) return;
    const start = (page - 1) * offset;
    dispatch(
      fetchActowizProducts({
        platform: p,
        start,
        offset,
        mode: "replace",
      })
    );
  }, [p, page, offset, dispatch]);

  const items = byPlatform?.[p] || [];
  const status = statusByPlatform?.[p] || "idle";
  const error = errorByPlatform?.[p] || "";

  const hasPrev = page > 1;
  const hasNext = items.length === offset;
  const getRequestKey = (targetPage) => `${p || "all"}:${(targetPage - 1) * offset}:${offset}`;

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 flex flex-col">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />

      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-10 md:px-6">
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900 md:text-3xl">
              {label} Products
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Browse products from {label}.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <button
              type="button"
              disabled={!hasPrev || status === "loading"}
              onClick={() =>
                setPage((v) => {
                  const nextPage = Math.max(1, v - 1);
                  dispatch(clearActowizRequest(getRequestKey(nextPage)));
                  return nextPage;
                })
              }
              className="rounded-full border border-slate-200 bg-white px-3 py-1.5 font-semibold text-slate-700 disabled:opacity-50"
            >
              Prev
            </button>
            <span className="text-slate-500">Page {page}</span>
            <button
              type="button"
              disabled={!hasNext || status === "loading"}
              onClick={() =>
                setPage((v) => {
                  const nextPage = v + 1;
                  dispatch(clearActowizRequest(getRequestKey(nextPage)));
                  return nextPage;
                })
              }
              className="rounded-full bg-[#4F46E5] px-3 py-1.5 font-semibold text-white disabled:opacity-50"
            >
              Next →
            </button>
          </div>
        </div>

        {status === "loading" && items.length === 0 && (
          <div className="flex justify-center py-16">
            <span className="text-sm text-slate-500">Loading…</span>
          </div>
        )}

        {error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 whitespace-pre-line">
            {error}
          </div>
        )}

        {!error && status !== "loading" && items.length === 0 && (
          <div className="flex justify-center py-16">
            <span className="text-sm text-slate-500">No products found.</span>
          </div>
        )}

        {items.length > 0 && (
          <>
            <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
              {items.map((product, idx) => (
                <ProductCard
                  key={`${product.pid || product.sku}-${idx}`}
                  accent={["orange", "blue", "green", "purple"][idx % 4]}
                  product={product}
                  showComparison={false}
                  retailerLabel={PLATFORM_LABELS[p]}
                  onClick={() =>
                    navigate(`/product/${encodeURIComponent(product.pid || product.sku)}`)
                  }
                />
              ))}
            </div>

            {/* Pagination controls shown in header */}
          </>
        )}
      </main>

      <Footer />
    </div>
  );
}

export default PlatformProducts;

